import FieldFrame, { describedBy } from './FieldFrame';
import { cx } from '../lib/cx';

interface NumberInputProps {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  value: number | '';
  onValueChange: (value: number | '') => void;
  min?: number;
  max?: number;
  step?: number;
  /** Unit shown inside the control: seats, sf, doors. */
  suffix?: string;
}

const stepBtn =
  'flex min-h-tap w-tap shrink-0 items-center justify-center border-grey-300 ' +
  'text-2xl leading-none text-ink hover:bg-grey-050 ' +
  'disabled:cursor-not-allowed disabled:opacity-40';

/**
 * A number field with full-size minus and plus keys. The stock browser spinner
 * is a four-pixel target; this audience is on phones, frequently older, often
 * in a parking lot after a meeting.
 */
export default function NumberInput({
  id,
  label,
  hint,
  error,
  value,
  onValueChange,
  min,
  max,
  step = 1,
  suffix,
}: NumberInputProps) {
  const current = value === '' ? 0 : value;
  const atMin = min !== undefined && current - step < min;
  const atMax = max !== undefined && current + step > max;

  const nudge = (delta: number) => {
    let next = current + delta;
    if (min !== undefined) next = Math.max(min, next);
    if (max !== undefined) next = Math.min(max, next);
    onValueChange(next);
  };

  return (
    <FieldFrame id={id} label={label} hint={hint} error={error}>
      <div
        className={cx(
          'flex items-stretch overflow-hidden rounded-sm border bg-white',
          error ? 'border-alert' : 'border-grey-300',
        )}
      >
        <button
          type="button"
          className={cx(stepBtn, 'border-r')}
          onClick={() => nudge(-step)}
          disabled={value !== '' && atMin}
          aria-label={`Decrease ${label.toLowerCase()}`}
        >
          &minus;
        </button>

        <div className="flex min-w-0 flex-1 items-center gap-2 px-3">
          <input
            id={id}
            type="number"
            inputMode="numeric"
            value={value}
            min={min}
            max={max}
            step={step}
            aria-describedby={describedBy(id, hint, error)}
            aria-invalid={error ? true : undefined}
            onChange={(e) => onValueChange(e.target.value === '' ? '' : Number(e.target.value))}
            className="t-numeral w-full min-w-0 bg-transparent py-2 text-lg text-ink outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
          {suffix && (
            <span className="t-meta shrink-0 text-grey-600" aria-hidden="true">
              {suffix}
            </span>
          )}
        </div>

        <button
          type="button"
          className={cx(stepBtn, 'border-l')}
          onClick={() => nudge(step)}
          disabled={value !== '' && atMax}
          aria-label={`Increase ${label.toLowerCase()}`}
        >
          +
        </button>
      </div>
    </FieldFrame>
  );
}
